// src/pages/Payroll/index.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Box, Card, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem,
  CircularProgress, Stack, Chip, Alert, Paper, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, InputAdornment
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SecurityIcon from '@mui/icons-material/Security';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';

import PayslipModal from './PayslipModal';

const getApiBase = () => {
  return window.location.origin.includes('localhost')
    ? 'http://localhost:5000'
    : window.location.origin;
};

const PayrollMaster = () => {
  const [month, setMonth] = useState(9);
  const [year, setYear] = useState(2026);
  const [category, setCategory] = useState('ALL');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [notification, setNotification] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState('ALL');

  // Edit Existing Modal States
  const [editOpen, setEditOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editPresent, setEditPresent] = useState(0);
  const [editLeave, setEditLeave] = useState(0);
  const [editAbsent, setEditAbsent] = useState(0);
  const [editRemarks, setEditRemarks] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Manual Add Modal States
  const [manualOpen, setManualOpen] = useState(false);
  const [manualEmpCode, setManualEmpCode] = useState('');
  const [manualPresent, setManualPresent] = useState(30);
  const [manualLeave, setManualLeave] = useState(0);
  const [manualAbsent, setManualAbsent] = useState(0);
  const [manualRemarks, setManualRemarks] = useState('Manually added by HR');
  const [savingManual, setSavingManual] = useState(false);

  // Payslip Modal States
  const [payslipOpen, setPayslipOpen] = useState(false);
  const [payslipData, setPayslipData] = useState(null);
  const [loadingSlipEmp, setLoadingSlipEmp] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      }
    };
  };

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const apiHost = getApiBase();
      const categoryParam = (category === 'ALL' || category === 'All Categories') ? '' : category;
      const queryStr = `month=${month}&year=${year}${categoryParam ? `&employmentType=${categoryParam}` : ''}`;
      
      const endpoints = [
        `${apiHost}/payroll?${queryStr}`,
        `${apiHost}/api/payroll?${queryStr}`,
        `/payroll?${queryStr}`,
        `/api/payroll?${queryStr}`
      ];

      let found = null;
      for (const ep of endpoints) {
        try {
          const res = await axios.get(ep, getAuthHeaders());
          const list = Array.isArray(res.data) ? res.data : res.data?.records || res.data?.data || [];
          if (Array.isArray(list) && list.length > 0) {
            found = list;
            break;
          } else if (Array.isArray(list)) {
            found = list;
          }
        } catch (e) {}
      }

      setRecords(found || []);
    } catch (err) {
      console.error('Fetch payroll failed:', err);
      setRecords([]);
      if (err.response?.status === 403) {
        setNotification({ type: 'error', text: 'Access Denied: SuperAdmin authorization required.' });
      }
    } finally {
      setLoading(false);
    }
  }, [month, year, category]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Bulk Generator (Only generates employees with punches/OD)
  const handleGenerate = async () => {
    setGenerating(true);
    setNotification(null);
    const apiHost = getApiBase();
    const payload = { month, year, employmentType: category };

    const candidates = [
      `${apiHost}/payroll/generate`,
      `${apiHost}/api/payroll/generate`,
      `/payroll/generate`,
      `/api/payroll/generate`
    ];

    let successResponse = null;
    let lastError = null;

    for (const url of candidates) {
      try {
        const res = await axios.post(url, payload, getAuthHeaders());
        successResponse = res;
        break;
      } catch (err) {
        lastError = err;
        if (err.response?.status !== 405 && err.response?.status !== 404) break;
      }
    }

    if (successResponse) {
      setNotification({
        type: 'success',
        text: successResponse.data?.message || 'Payroll generated successfully!'
      });
      fetchRecords();
    } else {
      setNotification({
        type: 'error',
        text: lastError?.response?.data?.message || 'Failed to generate payroll'
      });
    }
    setGenerating(false);
  };

  // Manual Add Employee to Payroll
  const handleSaveManualAdd = async () => {
    if (!manualEmpCode.trim()) {
      alert('Please enter an Employee Code');
      return;
    }
    setSavingManual(true);
    try {
      const apiHost = getApiBase();
      const payload = {
        empCode: manualEmpCode.trim(),
        month,
        year,
        presentDays: Number(manualPresent),
        leaveDays: Number(manualLeave),
        absentDays: Number(manualAbsent),
        hrRemarks: manualRemarks
      };

      const candidates = [
        `${apiHost}/payroll/manual-add`,
        `${apiHost}/api/payroll/manual-add`,
        `/payroll/manual-add`,
        `/api/payroll/manual-add`
      ];

      let res = null;
      for (const ep of candidates) {
        try {
          res = await axios.post(ep, payload, getAuthHeaders());
          break;
        } catch (e) {}
      }

      if (res && res.data?.success) {
        setNotification({
          type: 'success',
          text: res.data.message || `Added employee ${manualEmpCode} to payroll.`
        });
        setManualOpen(false);
        setManualEmpCode('');
        fetchRecords();
      } else {
        throw new Error('Server returned an error');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Employee not found in master records.');
    } finally {
      setSavingManual(false);
    }
  };

  // Edit Existing Record
  const handleOpenEdit = (rec) => {
    setEditingRecord(rec);
    setEditPresent(rec.presentDays || 0);
    setEditLeave(rec.leaveDays || 0);
    setEditAbsent(rec.absentDays || 0);
    setEditRemarks(rec.hrRemarks || '');
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const apiHost = getApiBase();
      const payload = {
        empCode: editingRecord.empCode,
        month,
        year,
        presentDays: Number(editPresent),
        leaveDays: Number(editLeave),
        absentDays: Number(editAbsent),
        hrRemarks: editRemarks
      };

      const endpoints = [
        `${apiHost}/payroll/update-days`,
        `${apiHost}/api/payroll/update-days`,
        `/payroll/update-days`,
        `/api/payroll/update-days`
      ];

      let res = null;
      for (const ep of endpoints) {
        try {
          res = await axios.put(ep, payload, getAuthHeaders());
          break;
        } catch (e) {}
      }

      if (res && res.data?.success) {
        setNotification({
          type: 'success',
          text: `Updated attendance for ${editingRecord.name} (${editingRecord.empCode})`
        });
        setEditOpen(false);
        fetchRecords();
      } else {
        throw new Error('Save adjustment failed');
      }
    } catch (err) {
      setNotification({
        type: 'error',
        text: err.response?.data?.message || 'Failed to update record'
      });
    } finally {
      setSavingEdit(false);
    }
  };

  // View Payslip Modal
  const handleViewPayslip = async (rec) => {
    setLoadingSlipEmp(rec.empCode);
    const apiHost = getApiBase();
    const targetMonth = rec.month || month;
    const targetYear = rec.year || year;

    const endpoints = [
      `${apiHost}/payroll/slip/${rec.empCode}?month=${targetMonth}&year=${targetYear}`,
      `${apiHost}/api/payroll/slip/${rec.empCode}?month=${targetMonth}&year=${targetYear}`,
      `/payroll/slip/${rec.empCode}?month=${targetMonth}&year=${targetYear}`,
      `/api/payroll/slip/${rec.empCode}?month=${targetMonth}&year=${targetYear}`
    ];

    let fullSlipData = null;
    for (const ep of endpoints) {
      try {
        const res = await axios.get(ep, getAuthHeaders());
        if (res.data && (res.data.payroll || res.data.master || res.data.employee)) {
          fullSlipData = res.data;
          break;
        }
      } catch (err) {}
    }

    setLoadingSlipEmp(null);

    if (fullSlipData) {
      setPayslipData(fullSlipData);
      setPayslipOpen(true);
    } else {
      setPayslipData({ payroll: rec, master: rec, employee: rec });
      setPayslipOpen(true);
    }
  };

  const uniqueProjects = useMemo(() => {
    const set = new Set();
    records.forEach(r => { if (r.project) set.add(r.project); });
    return Array.from(set).sort();
  }, [records]);

  const filteredRecords = useMemo(() => {
    return records.filter(r => {
      const matchSearch =
        String(r.empCode || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(r.name || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchProject = selectedProject === 'ALL' || r.project === selectedProject;
      return matchSearch && matchProject;
    });
  }, [records, searchTerm, selectedProject]);

  const exportExcel = () => {
    if (!Array.isArray(filteredRecords) || filteredRecords.length === 0) return;
    const exportData = filteredRecords.map((r) => ({
      'Emp Code': r.empCode,
      'Name': r.name,
      'Designation': r.designation,
      'Division': r.division,
      'Project': r.project,
      'Category': r.employmentType,
      'Calendar Days': r.workingDays,
      'Present Days': r.presentDays,
      'Paid Leave': r.leaveDays,
      'Cut / LOP Days': r.absentDays,
      'Payable Days': r.actualDays,
      'Total Earnings (INR)': r.totalEarnings,
      'Total Deductions (INR)': r.totalDeductions,
      'Net Payment (INR)': r.netPayment,
      'HR Audit Remarks': r.hrRemarks || 'Normal',
      'Status': r.status
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Payroll_${category}_${month}_${year}`);
    XLSX.writeFile(wb, `GDCL_Payroll_${category}_${month}_${year}.xlsx`);
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, pt: { xs: 10, md: 12 }, bgcolor: '#f8fafc', minHeight: '100vh', width: '100%' }}>
      <Card sx={{ maxWidth: 1450, mx: 'auto', p: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
        
        {/* Header Actions */}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3} spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SecurityIcon sx={{ color: '#dc2626' }} />
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#0f172a' }}>
                Payroll Engine & HR Settlement Center
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Automatic biometric sync, manual additions, attendance adjustment, and official payslips
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5}>
            {/* MANUAL ADD BUTTON */}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PersonAddAlt1Icon />}
              onClick={() => setManualOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Add Employee to Payroll
            </Button>

            {/* GENERATE BUTTON */}
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
              onClick={handleGenerate}
              disabled={generating}
              sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, textTransform: 'none', px: 2.5, fontWeight: 600 }}
            >
              {generating ? 'Processing...' : `Generate Verified Payroll`}
            </Button>

            {/* EXPORT EXCEL */}
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={exportExcel}
              disabled={filteredRecords.length === 0}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Export Register (.xlsx)
            </Button>
          </Stack>
        </Stack>

        {notification && (
          <Alert severity={notification.type} sx={{ mb: 3 }} onClose={() => setNotification(null)}>
            {notification.text}
          </Alert>
        )}

        {/* Filter Toolbar */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: '#f1f5f9', borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search Emp Code or Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ bgcolor: 'white' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Project"
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                sx={{ bgcolor: 'white' }}
              >
                <MenuItem value="ALL">All Projects</MenuItem>
                {uniqueProjects.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Grid>

            <Grid item xs={6} sm={2.5}>
              <TextField
                select
                fullWidth
                size="small"
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                sx={{ bgcolor: 'white' }}
              >
                <MenuItem value="ALL">All Categories</MenuItem>
                <MenuItem value="Regular">Regular</MenuItem>
                <MenuItem value="Probation">Probation</MenuItem>
                <MenuItem value="Temporary">Temporary</MenuItem>
                <MenuItem value="Retainership">Retainership</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={6} sm={2.5}>
              <TextField
                select
                fullWidth
                size="small"
                label="Month"
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                sx={{ bgcolor: 'white' }}
              >
                {[...Array(12)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('en', { month: 'long' })}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Year"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                sx={{ bgcolor: 'white' }}
              >
                {[2025, 2026, 2027].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {/* Records Table */}
        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ color: '#64748b', mt: 2 }}>Loading verified payroll data...</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Emp Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Employee Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Category</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Days (P / L / Cut)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Payable Days</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Earnings (₹)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Deductions (₹)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Net Pay (₹)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>HR Remarks</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 5, color: '#94a3b8' }}>
                      No payroll records generated yet for this filter. Click "Generate Verified Payroll" or "Add Employee to Payroll".
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRecords.map((r) => {
                    const hasSalaryCut = Number(r.absentDays || 0) > 0;
                    return (
                      <TableRow key={r._id || r.empCode} hover sx={{ bgcolor: hasSalaryCut ? '#fff7ed' : 'inherit' }}>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.empCode}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.designation}</Typography>
                        </TableCell>
                        <TableCell><Chip label={r.project || 'KTOF'} size="small" variant="outlined" /></TableCell>
                        <TableCell><Chip label={r.employmentType} size="small" /></TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                            <Tooltip title="Present Days">
                              <Chip label={`P:${r.presentDays || 0}`} size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600 }} />
                            </Tooltip>
                            <Tooltip title="Paid Leave Days">
                              <Chip label={`L:${r.leaveDays || 0}`} size="small" sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }} />
                            </Tooltip>
                            {hasSalaryCut && (
                              <Tooltip title="Loss of Pay / Cut Days">
                                <Chip label={`Cut:${r.absentDays}`} size="small" sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 700 }} />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          {r.actualDays} / {r.workingDays}
                        </TableCell>
                        <TableCell align="right">₹{Number(r.totalEarnings || 0).toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ color: '#dc2626' }}>-₹{Number(r.totalDeductions || 0).toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#0284c7' }}>
                          ₹{Number(r.netPayment || 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption" sx={{ color: hasSalaryCut ? '#c2410c' : '#16a34a', fontWeight: 600 }}>
                            {r.hrRemarks || 'Normal'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Edit Attendance & Leaves">
                              <IconButton size="small" onClick={() => handleOpenEdit(r)} sx={{ color: '#f59e0b' }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Official Salary Slip">
                              <IconButton 
                                size="small" 
                                onClick={() => handleViewPayslip(r)} 
                                sx={{ color: '#2563eb' }}
                                disabled={loadingSlipEmp === r.empCode}
                              >
                                {loadingSlipEmp === r.empCode ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  <VisibilityIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* DIALOG 1: MANUAL ADD EMPLOYEE (EVEN WITH 0 PUNCHES) */}
      <Dialog open={manualOpen} onClose={() => setManualOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Add Employee to {month}/{year} Payroll
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Add any employee directly from Employee Master (e.g. for site transfer, manual attendance, or zero-attendance recording).
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Employee Code"
                placeholder="e.g. 100454 or 115061"
                value={manualEmpCode}
                onChange={(e) => setManualEmpCode(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Present Days"
                type="number"
                value={manualPresent}
                onChange={(e) => setManualPresent(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Paid Leave Days"
                type="number"
                value={manualLeave}
                onChange={(e) => setManualLeave(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Cut (LOP) Days"
                type="number"
                value={manualAbsent}
                onChange={(e) => setManualAbsent(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="HR Audit Remarks"
                value={manualRemarks}
                onChange={(e) => setManualRemarks(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setManualOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveManualAdd}
            disabled={savingManual}
            sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
          >
            {savingManual ? 'Adding...' : 'Add to Payroll'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG 2: EDIT ATTENDANCE & LEAVES */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Adjust Attendance: {editingRecord?.name} ({editingRecord?.empCode})
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Present Days"
                type="number"
                value={editPresent}
                onChange={(e) => setEditPresent(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Paid Leave Days"
                type="number"
                value={editLeave}
                onChange={(e) => setEditLeave(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Cut (LOP) Days"
                type="number"
                value={editAbsent}
                onChange={(e) => setEditAbsent(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                value={editRemarks}
                onChange={(e) => setEditRemarks(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={savingEdit}
            sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
          >
            {savingEdit ? 'Updating...' : 'Save & Recalculate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Official Payslip View Modal */}
      {payslipOpen && payslipData && (
        <PayslipModal
          open={payslipOpen}
          onClose={() => {
            setPayslipOpen(false);
            setPayslipData(null);
          }}
          data={payslipData}
        />
      )}
    </Box>
  );
};

export default PayrollMaster;